/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IAddressAddItem, IAddressAddUpdateProps } from '@msdyn365-commerce-modules/address';
import { IBusinessAccountAddressViewProps } from './coc-c-business-account-address';
import classnames from 'classnames';

/**
 * Address Add Update Component.
 * @param param0 - Root param.
 * @param param0.AddressForm - Address form.
 * @param param0.heading - Address Heading.
 * @param param0.items - IAddressAddItem[].
 * @param param0.hasError - Boolean.
 * @param param0.error - IAddressError.
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns - AddressForm Node.
 */
const AddressAddUpdate: React.FC<IAddressAddUpdateProps> = ({
    AddressForm,
    heading,
    items,
    hasError,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton
}) => {
    const renderAddressItems = (addressItems: IAddressAddItem[]) => {
        return addressItems.map(({ AddressItem, key, label, alert, input }) => {
            return (
                <Node className={classnames({ ...AddressItem }, 'col-md-4')} key={key}>
                    {label}
                    {hasError && alert}
                    {input}
                </Node>
            );
        });
    };

    // Split the items array into firstItems and lastItems
    const firstItems = items.slice(0, 3);
    const lastItems = items.slice(3);

    return (
        <Node {...AddressForm}>
            {heading}
            <div className='container-fluid'>
                <div className='row'>{renderAddressItems(firstItems)}</div>
                <div className='row'>{renderAddressItems(lastItems)}</div>
            </div>

            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

/**
 * Business Account Address View Component.
 * @param props - Props.
 * @returns - BusinessAccountAddress Module.
 */
const BusinessAccountAddressView: React.FC<IBusinessAccountAddressViewProps> = props => {
    const { BusinessAccountAddress, showAddOrUpdateAddress } = props;
    return (
        <Module {...BusinessAccountAddress}>
            <AddressAddUpdate {...showAddOrUpdateAddress} />
        </Module>
    );
};

export default BusinessAccountAddressView;
